import { Avatar, Group, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { FoundPersonSummary, UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { Button } from 'src/design-system/Button';
import { ConfirmModal } from 'src/design-system/ConfirmModal';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { AddFoundPersonModal } from './AddFoundPersonModal';
import { FoundPersonDrawer } from './FoundPersonDrawer';
import { initials, splitName } from './foundPersonDetails';
import { useDismissFoundPerson } from './useFoundPerson';

type Props = {
  file: UploadedFile;
  person: FoundPersonSummary;
  relativeFirstName: string | null;
};

export function FoundPersonRow({ file, person, relativeFirstName }: Props) {
  const [drawerOpened, drawer] = useDisclosure(false);
  const [confirmOpened, confirm] = useDisclosure(false);
  const [addOpened, addModal] = useDisclosure(false);
  const dismiss = useDismissFoundPerson(file, person.id);
  const itemCount = person.ownCount + person.sharedCount;

  const handleDismiss = () =>
    dismiss.mutate(undefined, {
      onSuccess: () => {
        confirm.close();
        drawer.close();
      },
    });

  // Names who is being added: beside a list of what was found, "Add to John's family" reads as adding the data.
  const { first } = splitName(person.name);
  const addLabel = `Add ${first} to ${relativeFirstName ? `${relativeFirstName}'s` : 'the'} family`;
  const handleAdd = () => {
    drawer.close();
    addModal.open();
  };

  return (
    <Group
      bg="gray.0"
      px="sm"
      py="xs"
      gap="xs"
      w="100%"
      style={{ borderRadius: 20 }}
      data-testid="found-person-row"
    >
      <Avatar size={32} color="brand" radius="xl">
        {initials(person.name)}
      </Avatar>
      <Stack gap={0} style={{ flex: 1, minWidth: 200 }}>
        <Text size="sm" fw={FONT_WEIGHTS.medium} c="black">
          {person.name} isn&apos;t a client
        </Text>
        <Text size="xs" c="gray.7">
          {itemCount} item{itemCount === 1 ? '' : 's'} kept aside
          {person.relationship ? ` · described as ${person.relationship}` : ''}
        </Text>
      </Stack>
      <Group gap="xxs" wrap="wrap">
        <Button variant="tertiary" fz="xs" onClick={drawer.open}>
          See what we found
        </Button>
        <Button variant="tertiary" fz="xs" onClick={confirm.open}>
          Not a client
        </Button>
        <Button variant="primary" size="xs" onClick={handleAdd}>
          {addLabel}
        </Button>
      </Group>

      <FoundPersonDrawer
        file={file}
        person={person}
        relativeFirstName={relativeFirstName}
        opened={drawerOpened}
        onClose={drawer.close}
        onNotAClient={confirm.open}
        onAdd={handleAdd}
        addLabel={addLabel}
      />
      <AddFoundPersonModal
        file={file}
        person={person}
        relativeFirstName={relativeFirstName}
        opened={addOpened}
        onClose={addModal.close}
      />
      <ConfirmModal
        opened={confirmOpened}
        onClose={confirm.close}
        onConfirm={handleDismiss}
        title={`${person.name} isn't a client?`}
        description={`This deletes what we kept about ${person.name} from this document. Items shared with ${
          relativeFirstName ?? 'the client'
        } keep ${person.name} as an outside owner.`}
        confirmLabel="Not a client"
        error={dismiss.error}
        isLoading={dismiss.isPending}
      />
    </Group>
  );
}
